import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FindingConfigItem } from '../../models/inspection-config.model';
import { InspectionFinding } from '../../models/inspection-finding.model';
import {
  FindingDetailModalComponent,
  FindingDetailDialogData,
  FindingDetailDialogResult
} from '../finding-detail-modal/finding-detail-modal.component';

@Component({
  selector: 'app-findings-section',
  templateUrl: './findings-section.component.html',
  standalone: false
})
export class FindingsSectionComponent implements OnChanges {

  @Input() items: FindingConfigItem[] = [];
  @Input() findingType: 'PEST' | 'DISEASE' | 'DEFICIENCY' = 'PEST';
  @Input() viewOnly: boolean = false;
  @Input() tenantId: number = 0;
  @Input() reportId: string = '';
  /**
   * Pre-loaded findings from an existing report — passed in by the parent form
   * after loadReportDetail resolves. The component hydrates selectedFindings from
   * this input whenever it changes.
   */
  @Input() preloadedFindings: InspectionFinding[] = [];

  /**
   * Fired when the user changes a selection. selectedFindings lives outside the parent's
   * FormGroup, so without this the form never registers as dirty and a save triggered by
   * step navigation skips silently. Hydration from preloaded data deliberately does not
   * fire it — loading a report is not a user edit.
   */
  @Output() changed = new EventEmitter<void>();

  // Tracks which items are selected and their captured details
  selectedFindings: Map<string, InspectionFinding> = new Map();

  constructor(private dialog: MatDialog) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Re-hydrate when preloadedFindings or items arrive (either order)
    if (changes['preloadedFindings'] || changes['items']) {
      this.hydrateFromPreloaded();
    }
  }

  /**
   * Populates selectedFindings from the preloaded API data.
   * Only runs when both items (config list) and preloadedFindings are non-empty,
   * so it handles the race between config loading and report detail loading.
   */
  private hydrateFromPreloaded(): void {
    if (!this.preloadedFindings?.length || !this.items?.length) return;

    // Only hydrate findings that match this section's type
    const relevant = this.preloadedFindings.filter(f => f.findingType === this.findingType);
    relevant.forEach(finding => {
      this.selectedFindings.set(finding.configItemId, finding);
    });
  }

  isSelected(itemId: string): boolean {
    return this.selectedFindings.has(itemId);
  }

  onItemClick(item: FindingConfigItem): void {
    const existing = this.selectedFindings.get(item.id);

    // Wire the first voice note URL for playback (mobile-recorded)
    const voiceNoteUrl = existing?.voiceNotes?.[0]?.downloadUrl ?? undefined;

    const data: FindingDetailDialogData = {
      item,
      findingType: this.findingType,
      tenantId: this.tenantId,
      reportId: this.reportId,
      existingNotes: existing?.notes,
      existingPhotos: existing?.photos,
      voiceNoteUrl,
      viewOnly: this.viewOnly
    };

    this.dialog.open(FindingDetailModalComponent, {
      data,
      width: '520px',
      autoFocus: false
    }).afterClosed().subscribe((result: FindingDetailDialogResult | null) => {
      if (result) {
        // Preserve voice notes from the existing finding — web can't record them
        const existingVoiceNotes = existing?.voiceNotes ?? [];
        this.selectedFindings.set(item.id, {
          id: existing?.id,
          findingType: result.findingType,
          configItemId: result.configItemId,
          notes: result.notes,
          photos: result.photos,
          voiceNotes: existingVoiceNotes
        });
        this.changed.emit();
      }
    });
  }

  removeSelection(itemId: string, event: Event): void {
    event.stopPropagation();
    this.selectedFindings.delete(itemId);
    this.changed.emit();
  }

  // Returns all selected findings as an array for the parent form to collect
  getFindings(): InspectionFinding[] {
    return Array.from(this.selectedFindings.values());
  }
}
